/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import { Component, Input, ViewChild, ElementRef, AfterViewInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SchemaField, Document, TextFieldFormat, DocumentSchema, SystemFieldType } from '../../../store/models/document.model';
import { DynamicField } from '../dynamic-field';
import { TranslateService } from '@ngx-translate/core';
import { ViewComponent } from '../../../store/models/workflow.model';
import { DocumentReader, FilterGroup, Filter, RuleOperator, FilterGroupType } from 'src/app/modules/core/store/models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject, Observable, of } from 'rxjs';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-reference-field',
    templateUrl: './reference-field.component.html',
    styleUrls: ['./reference-field.component.scss'],
    standalone: false
})
export class ReferenceFieldComponent implements DynamicField, OnDestroy {
  @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent;
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() set field(value: SchemaField) {
    this._field = value;
    this.fieldUId = value.uId;
  }
  get field(): SchemaField {
    return this._field;
  }
  _field: SchemaField;
  @Input() layerUId: string;
  @Input() set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  get document(): Document {
    return this.reader.document;
  }
  reader: DocumentReader;
  @Input() displayName: string;
  @Input() viewComponent: ViewComponent;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;
  showFieldAsEdit: boolean = false;
  fieldUId: string;

  documentsLoading: boolean;
  documents: any[];
  documentsInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  searchTerm: string = null;
  selectedDocument?: any;
  selectedDocumentId?: string;
  filterFields: string;
  relatedDataset: Dataset;
  placeholder: string;

  set value(val: any) {
    this.placeholder = this.translateService.instant('MANAGEMENT.DATASETS.REFERENCE-PLACEHOLDER');
    if (!!val) {
      this.selectedDocumentId = val;
    } else {
      this.selectedDocumentId = null;
      this.selectedDocument = null;
    }
    this.filterFields = this._field.settings.relatedFilterFields;
    if (!!!this.filterFields) {
      this.filterFields = 'name';
    }
    this.searchTerm = null;
  }
  get value(): any {
    return !!!this.selectedDocument?.uid ? null : this.selectedDocument?.uid;
  }

  _isEdit: boolean = false;
  @Input() set isEdit(edit: boolean) {
    this._isEdit = edit;

    this.calculateShouldDisplayFieldForEdit();
  }
  _value: string;

  isReadOnly = false;

  constructor(private translateService: TranslateService, private dataApiService: DataApiService) {}

  ngOnDestroy(): void {
    this.valueChanged.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchForReferenceObjects(searchTerm: string): void {
    this.getReferenceObjects(searchTerm)
      .pipe(take(1))
      .subscribe(objects => {
        this.documents = [];
        objects.map(o => this.documents.push({ ...o, _displayString: this.getDisplayString(o) }));
      });
  }

  onValueChanged(): void {
    this.valueChanged.emit(this);
  }

  private loadReferenceData(): void {
    this.getInitialReferenceObjects()
      .pipe(take(1))
      .subscribe(objects => {
        this.documents = [];
        objects.map(o => this.documents.push({ ...o, _displayString: this.getDisplayString(o) }));
      });
  }

  private getDisplayString(document: Document): string {
    const reader: DocumentReader = new DocumentReader(document);

    const filterFields = this.filterFields.split(',');
    const values = [];

    filterFields.map(f => values.push(reader.getFieldValue(f)));
    return values.join(' ');
  }

  private getReferenceObject(documentId: string): void {
    this.getReferenceObjectById(documentId)
      .pipe(take(1))
      .subscribe(documents => {
        const selectedDocument = documents?.length > 0 ? documents[0] : null;
        let valueForDisplay = this.getDisplayString(selectedDocument);
        this.selectedDocument = { ...selectedDocument, _displayString: valueForDisplay };
      });
  }

  private buildFieldQueryFilter(searchTerm: string): FilterGroup {
    const filterGroup: FilterGroup = {
      uId: null,
      filters: [],
      filterGroups: null,
      groupType: FilterGroupType.And
    };

    const filterFields = this.filterFields.split(',');
    if (filterFields.length == 1) {
      const filter: Filter = {
        field: this.filterFields,
        operator: RuleOperator.ContainsNoCase,
        value: `${searchTerm}%`
      };

      filterGroup.filters.push(filter);
    } else {
      const filterSubGroup: FilterGroup = {
        uId: null,
        filters: [],
        filterGroups: null,
        groupType: FilterGroupType.Or
      };
      filterFields.map(f => {
        const filter: Filter = {
          field: f,
          operator: RuleOperator.ContainsNoCase,
          value: `${searchTerm}%`
        };
        filterSubGroup.filters.push(filter);
      });

      filterGroup.filterGroups = [filterSubGroup];
    }

    return filterGroup;
  }

  private getInitialReferenceObjects(): Observable<Document[]> {
    this.documentsLoading = true;

    return this.dataApiService
      .getDocuments(this._field.settings.relatedDatasetUId, {
        pagination: {
          skip: 0,
          take: 50
        },
        fields: 'uid,' + this.filterFields,
        filterGroup: null,
        sort: this.filterFields + ' asc'
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          this.documentsLoading = false;
          return data;
        })
      );
  }

  private getReferenceObjects(searchTerm: string = null): Observable<Document[]> {
    this.documentsLoading = true;

    return this.dataApiService
      .getDocuments(this._field.settings.relatedDatasetUId, {
        pagination: {
          skip: 0,
          take: 20
        },
        fields: 'uid,' + this.filterFields,
        filterGroup: this.buildFieldQueryFilter(searchTerm)
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          this.documentsLoading = false;
          return data;
        })
      );
  }

  private getReferenceObjectById(documentId: string = null): Observable<Document[]> {
    this.documentsLoading = true;

    const filter: Filter = {
      field: 'uid',
      operator: RuleOperator.Equal,
      value: documentId
    };

    const filterGroup: FilterGroup = {
      uId: null,
      filters: [filter],
      filterGroups: null,
      groupType: FilterGroupType.And
    };

    return this.dataApiService
      .getDocuments(this._field.settings.relatedDatasetUId, {
        pagination: {
          skip: 0,
          take: 1
        },
        fields: 'uid,' + this.filterFields,
        filterGroup: filterGroup
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          this.documentsLoading = false;
          return data;
        })
      );
  }

  private calculateShouldDisplayFieldForEdit() {
    this.showFieldAsEdit = this._isEdit && !this.isReadOnly;
  }

  tpOnContentUpdated(): void {
    this.value = this.reader.getFieldValue(this.field?.systemName);

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly = true;
      this.calculateShouldDisplayFieldForEdit();
    }

    this.documents = [];
    this.loadReferenceData();

    if (this.selectedDocumentId) {
      this.getReferenceObject(this.selectedDocumentId);
    }

    this.documentsInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(searchTerm => {
      if (this.searchTerm != searchTerm) {
        this.searchForReferenceObjects(searchTerm);
        this.searchTerm = searchTerm;
      } else {
        this.documentsLoading = false;
      }
    });
  }
}
